<template>
    <ModalBase :show-modal="showModal" @close-modal="close" :auto-h="true" :narrow="true">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal">
                <template v-slot:title>
                    <div class="customer-modal--title" v-html="modalTitle" />
                    <div class="customer-modal--entrydata">
                        <div class="customer-modal--entrydata__item">
                            <div>
                                <translate>Added </translate>{{accountantConnection.created | formatDate}}
                                <span class="sub-separator" />
                                <translate>Last active </translate>{{customerDetails.user_last_login | formatDate}}
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:buttons>
                    <GekkoButton 
                        :text="$gettext('Disconnect')" 
                        icon="fa-solid fa-trash-can" 
                        variant="link" 
                        class="text-danger"
                        v-gk-confirm="{
                            title: $gettext('Disconnect from customer?'),
                            message: $gettext('Are you sure you want to disconnect from this customer?'),
                            options: {
                                variant: 'danger',
                                confirmText: $gettext('Disconnect')
                            },
                            callback: disconnect
                        }"
                        />
                </template>
            </TitleBar>
        </template>
        
        <template v-slot:maincontent>
            <CustomerOverview v-if="!loading" :customerDetails="customerDetails" :accountantConnection="accountantConnection" />
        </template>

        <template v-slot:actions>
            <div v-if="!loading" class="customer-modal-actions">
                <template v-if="isConnected">
                    <button @click="openControlPanel()" class="btn btn-sm btn-primary">{{ $gettext('Control Panel') }}</button>
                    <button @click="openSeeAccount()" class="btn btn-sm btn-primary">{{ $gettext('See Account') }}</button>
                </template>
                <template v-if="!isConnected">
                    <button @click="reconnect()" class="btn btn-sm btn-primary">{{ $gettext('Reconnect') }}</button>
                </template>
            </div>
        </template>
    </ModalBase>
</template>
<script>
import { mapGetters } from "vuex";
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import ModalBase from "@/components/base_components/ModalBase";
import CustomerOverview from "@/components/accountants/CustomerOverview";
import AccountantsService from "@/store/services/accountants.service";

export default {
    name: "CustomerModal",
    components: { ModalBase, TitleBar, GekkoButton, CustomerOverview },
    props: {
        showModalOn: {
            type: Array
        }
    },
    data () {
        return {
            showModal: false,
            customerDetails: {
                name: '',
                created_by: {
                    display_name: ''
                }
            },
            accountantConnection: {
                reference: ''
            },
            pendingInvites: [],
            privateMessage: false,
            loading: true
        }
    },
    computed: {
        ...mapGetters ({
            currentUser: 'main/getCurrentUser',
            visitingCompanyId: 'main/getVisitingCompanyId',
            getCompanyId: 'main/getCompanyId',
            preferredLanguage: 'main/getPreferredLanguage'
        }),
        isConnected () {
            return this.accountantConnection.status === "connected"
        },
        modalTitle () {
            let statusText
            if (this.accountantConnection.status === 'connected') statusText = this.$gettext('Connected')
            if (this.accountantConnection.status === 'disconnected') statusText = this.$gettext('Disconnected')
            if (this.accountantConnection.status === 'pending') statusText = this.$gettext('Invite pending to')
            return statusText + ' ' + this.$gettext('customer') + ' ' + this.accountantConnection.reference
        }
    },
    watch: {
        $route: {
            immediate: true,
            handler(to) {
                if (this.$route.params['customerId']) {
                    AccountantsService.getCustomerInformation(this.$route.params['customerId']).then((response) => {
                        this.customerDetails = response.data

                        AccountantsService.getAccountantConnectionList({ customer: this.customerDetails.id }).then((response) => {
                            this.accountantConnection = response.data[0] // there must be just one connection per customer
                            AccountantsService.getPendingInvites().then((response) => {
                                if (response.data.find(o => o.invite_to === this.customerDetails.email) 
                                    && this.accountantConnection.status !== 'connected')
                                        this.accountantConnection.status = 'pending'
                            })
                        })
                        this.loading = false
                    })
                }

                this.showModal = this.showModalOn.includes(to.name);
                (this.showModal) ? document.body.classList.add('modal-on') : document.body.classList.remove('modal-on');
            }
        }
    },
    created () {
        window.addEventListener('keydown', this.closeEventListener)
    },
    destroyed () {
        window.removeEventListener('keydown', this.closeEventListener)
    },
    methods: {
        closeEventListener (event) {
            if (this.showModal && event.key === 'Escape') this.close()
        },
        close () {
            this.customerDetails = {
                name: '',
                created_by: {
                    display_name: ''
                }
            }
            this.accountantConnection = {
                reference: ''
            }
            this.$router.push({ name: 'Customers' })
        },
        disconnect () {
            AccountantsService.disconnect(this.accountantConnection.id).then((response) => {
                this.$emit("disconnected", response.data);
                this.$store.commit("setAlert", {
                    title: this.$gettext("Success!"),
                    text: this.$gettext("Your account have been disconnected from  ") + response.data.name + ".",
                    variant: "success",
                    duration: 4
                })
                this.close()
            })
        },
        reconnect () {
            if (!this.currentUser.is_gekko_accountant) {
                let data = {
                    accountant: this.accountantConnection.accountant,
                    invite_to: this.customerDetails.email,
                    invite_to_name: this.customerDetails.name,
                    message: this.$gettext("An accountant would like to connect to you."),
                    company_profile: this.getCompanyId
                }

                let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                if (re.test(data.invite_to)) {
                    AccountantsService.sendInvite(data).then((response) => {
                        this.$store.commit("setAlert", {
                            title: this.$gettext("Success!"),
                            text: this.$gettext("Invite sent to ") + response.data.invite_to_name + ".",
                            variant: "success",
                            duration: 4
                        });
                        this.$emit("refreshlist", false);
                        this.close();
                    }).catch((error) => {
                        this.$store.commit("setAlert", {
                            title: this.$gettext("Pending invitation"),
                            text: error,
                            variant: "warning",
                            duration: 4
                        });
                    });
                } else {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Error"),
                        text: this.$gettext("Email incorrect or not set."),
                        variant: "danger",
                        duration: 4
                    });
                }
            } else {
                AccountantsService.createGekkoConnection(this.getCompanyId, this.customerDetails.id).then((response) => {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Success!"),
                        text: this.$gettext("Connected to ") + response.data.name + ".",
                        variant: "success",
                        duration: 4
                    })
                    this.close()
                }).catch((response) => {
                    this.$store.commit("setAlert", {
                        title: this.$gettext("Error"),
                        text: response,
                        variant: "error",
                        duration: 4
                    });
                });
            }
        },
        openControlPanel() {
            this.$router.push({ name: 'ControlPanelSummary', params: {customerId: this.visitingCompanyId} })
        },
        openSeeAccount() {
            // TODO this
            window.location.href = '/' + this.preferredLanguage + '/dashboard?vc_id=' + this.$route.params['customerId']
        }
    }
}
</script>
<style lang="scss" scoped>
.log-input {
    margin-right: 5px;
}

.save-log-button {
    margin-left: 5px;
    min-width: 80px;
}

.flex-row-container {
    display: flex;
    flex-direction: row;
}

.customer-modal {
    &--title {
        line-height: 1;
        font-size: 26px;
        &:first-letter {
            text-transform: capitalize;
        }
        .object-grammar {
            text-transform: capitalize;
        }
        .object-typename {
            text-transform: lowercase;
        }
        .object-name {
            &.invoice {
                color: $colorAppInvoices;
            }
            &.cost {
                color: $colorAppCosts;
            }
        }
    }
    &--entrydata {
        margin-top: 7px;
        display: flex;
        align-items: center;
        font-size: 14px; 
        font-family: DINNextLTPro-Regular;
        color: #adadad;
        &__item {
            display: flex;
            align-items: center;
            line-height: 1;
        }
        .sub-separator {
            position:relative;
            padding: 0px 7px;
            &:before {
                content: '';
                width: 4px;
                height: 4px;
                border-radius: 4px;
                background: currentColor;
                position: absolute;
                top: 50%;
                left: 5px;
            }
        }
    }
}
.customer-modal-actions {
    font-size: 16px;
    display: flex;
    width: 100%;
    justify-content: center;
    align-content: center;
    > a { display: flex; align-items: center; }
    > * {
        &:not(:last-child) {
            margin-right: 10px;
        }
    }
}
</style>